// recovery-orphan-intent-settle.ts
//
// Orchestrator recovery (3.40.0, review G2 M1): a recovery intent without a
// result is normally resumed while its stop still stands. If the operator
// unblocks/retries the source first, the stop no longer stands and nothing
// would ever finish the op — yet the ticket file may already be committed
// (crash between the git commit and `applied`): G2 would count it as plan
// content and the scheduler would run it unrecognised. Settle every such
// orphan, without writing any new ticket:
//   - the recovery ticket for this label is in HEAD ⇒ `applied` (+ `todo`,
//     + the hash of the content at HEAD, H-1):
//     the controller did write it, so it is recognised (D2.6);
//   - otherwise ⇒ clean the two paths, then `failed` (D2.5 order).
import { join } from "node:path";
import { PLAN_INDEX_PATH } from "./controller-plan-index-markdown.js";
import { RECOVERY_TICKET_OP_KIND } from "./recovery-guards.js";
import type { RecoveryJournalView } from "./recovery-ledger-from-journal.js";
import { commitRecoveryTicketApplied, commitRecoveryTicketFailed } from "./recovery-op-journal.js";
import { discardRecoveryAttemptFiles } from "./recovery-ticket-attempt-cleanup.js";
import type { RecoveryMaterializeContext } from "./recovery-ticket-materializer-types.js";
import {
  isRecoveryTicketFor,
  type RecoveryTicketTrigger,
  recoverySourceLabel,
  recoveryTicketTitle,
} from "./recovery-ticket-shape.js";
import { ticketContentSha256 } from "./ticket-content-sha256.js";
import { ticketFileName } from "./ticket-file-name.js";
import { loadTicketsAtHead } from "./ticket-files-at-head.js";

export async function settleOrphanRecoveryIntents(
  ctx: RecoveryMaterializeContext,
  view: RecoveryJournalView,
): Promise<void> {
  const standing = new Set(view.scan.standing.map((s) => s.seq));
  const orphans = view.ledger.filter(
    (r) =>
      r.operation_kind === RECOVERY_TICKET_OP_KIND &&
      r.result === undefined &&
      !standing.has(r.trigger_seq),
  );
  if (orphans.length === 0) return;
  const atHead = await loadTicketsAtHead(ctx.gitRunner, ctx.repoDir);
  for (const op of orphans) {
    // The ledger drops the `other` trigger, so this is one of the three codes.
    const trigger = op.trigger as RecoveryTicketTrigger;
    const id = op.ticket_id;
    const committed = atHead.find((t) => t.id === id);
    if (
      id !== undefined &&
      committed !== undefined &&
      isRecoveryTicketFor(committed, recoverySourceLabel(op.source_ticket, trigger))
    ) {
      // H-1: pin the content at HEAD — only commit-owner commits there.
      await commitRecoveryTicketApplied(
        ctx.store,
        op.operation_id,
        id,
        ticketContentSha256(committed),
      );
      continue;
    }
    if (id !== undefined && committed === undefined) {
      const title = recoveryTicketTitle(op.source_ticket, trigger);
      const ticketPath = join(".avatar", "tickets", ticketFileName({ id, title }));
      await discardRecoveryAttemptFiles(ctx.gitRunner, ctx.repoDir, [ticketPath, PLAN_INDEX_PATH]);
    }
    await commitRecoveryTicketFailed(ctx.store, op.operation_id);
  }
}
